import { z } from 'zod';
/** Google Cloud (docs 67–71). */
export const GCP_KEY_MAX_CHARS = 8_000;
export const GCP_COMPUTE_SCOPE = 'https://www.googleapis.com/auth/compute';
export const GCP_TOKEN_URL = 'https://oauth2.googleapis.com/token';
export const GCP_COMPUTE_API = 'https://compute.googleapis.com/compute/v1';
const KeyFileSchema = z.object({
    type: z.literal('service_account'),
    project_id: z.string().regex(/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/),
    client_email: z.string().regex(/^[a-z0-9-]+@[a-z0-9-]+\.iam\.gserviceaccount\.com$/),
    private_key_id: z.string().regex(/^[a-f0-9]{20,64}$/),
    private_key: z.string().startsWith('-----BEGIN PRIVATE KEY-----').max(4_000),
});
/** Reads a service-account key file; a problem in words when it is not one. Nothing of the key is echoed. */
export function parseGcpServiceAccount(text) {
    let data;
    try {
        data = JSON.parse(text);
    }
    catch {
        return { problem: 'That is not the JSON key file from Google Cloud' };
    }
    const parsed = KeyFileSchema.safeParse(data);
    if (!parsed.success)
        return { problem: 'That is not a service-account key (type service_account, with project_id, client_email and private_key)' };
    const k = parsed.data;
    return { projectId: k.project_id, clientEmail: k.client_email, privateKeyId: k.private_key_id, privateKey: k.private_key };
}
/** The compact form Couch and the agent store. */
export function compactGcpKey(sa) {
    return JSON.stringify({ type: 'service_account', project_id: sa.projectId, client_email: sa.clientEmail, private_key_id: sa.privateKeyId, private_key: sa.privateKey });
}
export const SetGcpKeyRequestSchema = z.object({
    key: z.string().min(2).max(GCP_KEY_MAX_CHARS),
    acknowledged: z.literal(true, { message: 'Confirm that you understand what the key can do' }),
});
/** The Google Cloud console for a project's VMs. */
export function gcpConsoleUrl(projectId) {
    return `https://console.cloud.google.com/compute/instances?project=${encodeURIComponent(projectId)}`;
}
/** A quota as Google reports it. */
export const GcpQuotaSchema = z.object({ limit: z.number(), usage: z.number() });
/** A European zone with L4s, and its region's L4 quota for Spot and regular VMs (doc 68). */
export const GcpZoneOfferSchema = z.object({ zone: z.string(), region: z.string(), spotQuota: GcpQuotaSchema, quota: GcpQuotaSchema });
/** GET /api/v1/cloud/gcp/zones — `gpusAllRegions` is the project's overall GPU quota (0 = ask Google first). */
export const GcpZonesResponseSchema = z.object({
    zones: z.array(GcpZoneOfferSchema),
    gpusAllRegions: GcpQuotaSchema,
    projectId: z.string().nullable(),
    via: z.string(),
});
/** POST /api/v1/cloud/gcp/machines (doc 70): Spot by default; the disk is 100 GB. */
export const CreateGcpMachineRequestSchema = z.object({
    spot: z.boolean().default(true),
    name: z.string().trim().min(1).max(60).regex(/^[\p{L}\p{N} ._()-]+$/u, 'Letters, digits, spaces and . _ - ( ) only').optional(),
    /** The cost warning (doc 51) — the user has to tick it. */
    acknowledged: z.literal(true),
});
/** Google's console page of one VM (falls back to the VM list while the zone is not known). */
export function gcpInstanceConsoleUrl(project, zone, name) {
    const q = project ? `?project=${encodeURIComponent(project)}` : '';
    if (!zone)
        return `https://console.cloud.google.com/compute/instances${q}`;
    return `https://console.cloud.google.com/compute/instancesDetail/zones/${encodeURIComponent(zone)}/instances/${encodeURIComponent(name)}${q}`;
}
/** Google's GPU prices, and where a project asks for GPU quota. */
export const GCP_GPU_PRICES_URL = 'https://cloud.google.com/compute/gpus-pricing';
export function gcpQuotasUrl(project) {
    return `https://console.cloud.google.com/iam-admin/quotas${project ? `?project=${encodeURIComponent(project)}` : ''}`;
}
//# sourceMappingURL=gcp.js.map